import { defineStore } from 'pinia'
import { ref, type Ref } from 'vue'
import type { ApiResponse, PaginatedResponse, Pagination } from '@/app/types'
import api from '@/app/services/apiNir.ts'
import type { AxiosResponse } from 'axios'
import type {
    ServiceGuide,
    ServiceRequirement,
    ServiceStep,
    ServiceGuideCreateRequest,
    ServiceGuideUpdateRequest,
    ServiceRequirementCreateRequest,
    ServiceRequirementUpdateRequest,
    ServiceStepCreateRequest,
    ServiceStepUpdateRequest
} from '@/domains/home/services/nir/configurations/types/services.ts'

export const useServicesStore = defineStore("services", () => {
    const okResponse: Ref<object | undefined> = ref()
    const pagination: Ref<Pagination | undefined> = ref()
    const services: Ref<ServiceGuide[] | undefined> = ref()
    const requirements: Ref<ServiceRequirement[] | undefined> = ref()
    const steps: Ref<ServiceStep[] | undefined> = ref()

    // Service Guides
    const fetchServices = async (page: number, limit: number) => {
        return api.get(`/v1/configurations/services?page=${page}&limit=${limit}`)
            .then((response: AxiosResponse<ApiResponse<PaginatedResponse<ServiceGuide>>>) => {
                services.value = response.data.data.content
                pagination.value = response.data.data.pagination
            })
    }

    const createService = async (payload: ServiceGuideCreateRequest) => {
        return api.post("/v1/configurations/services", payload)
            .then((response: AxiosResponse<ApiResponse<object>>) => {
                okResponse.value = response.data.data
            })
    }

    const updateService = async (payload: ServiceGuideUpdateRequest) => {
        return api.put("/v1/configurations/services", payload)
            .then((response: AxiosResponse<ApiResponse<object>>) => {
                okResponse.value = response.data.data
            })
    }

    const removeService = async (id:string) => {
        return api.delete(`/v1/configurations/services/${id}`)
            .then((response: AxiosResponse<ApiResponse<object>>) => {
                okResponse.value = response.data.data
            })
    }

    // Requirements
    const fetchRequirements = async (serviceId: string) => {
        return api.get(`/v1/configurations/services/requirements?serviceId=${serviceId}`)
            .then((response: AxiosResponse<ApiResponse<{ data: ServiceRequirement[] }>>) => {
                requirements.value = response.data.data.data
            })
    }

    const createRequirement = async (payload: ServiceRequirementCreateRequest) => {
        return api.post("/v1/configurations/services/requirements", payload)
            .then((response: AxiosResponse<ApiResponse<object>>) => {
                okResponse.value = response.data.data
            })
    }

    const updateRequirement = async (payload: ServiceRequirementUpdateRequest) => {
        return api.put("/v1/configurations/services/requirements", payload)
            .then((response: AxiosResponse<ApiResponse<object>>) => {
                okResponse.value = response.data.data
            })
    }

    // Steps
    const fetchSteps = async (serviceId: string) => {
        return api.get(`/v1/configurations/services/steps?serviceId=${serviceId}`)
            .then((response: AxiosResponse<ApiResponse<{ data: ServiceStep[] }>>) => {
                steps.value = response.data.data.data
            })
    }

    const createStep = async (payload: ServiceStepCreateRequest) => {
        return api.post("/v1/configurations/services/steps", payload)
            .then((response: AxiosResponse<ApiResponse<object>>) => {
                okResponse.value = response.data.data
            })
    }

    const updateStep = async (payload: ServiceStepUpdateRequest) => {
        return api.put("/v1/configurations/services/steps", payload)
            .then((response: AxiosResponse<ApiResponse<object>>) => {
                okResponse.value = response.data.data
            })
    }

    return {
        okResponse,
        pagination,
        services,
        requirements,
        steps,
        fetchServices,
        createService,
        updateService,
        fetchRequirements,
        createRequirement,
        updateRequirement,
        fetchSteps,
        createStep,
        updateStep,
        removeService
    }
})
